import React, { useState, useRef } from 'react';

const HeroImagesManager = ({ hero_images: initialImages = [] }) => {
  const [images, setImages] = useState(initialImages);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const csrfToken = () =>
    document.querySelector('meta[name="csrf-token"]')?.content || '';

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');

    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch('/admin/hero_images', {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken() },
          body: formData,
        });
        if (!res.ok) throw new Error('Erro ao enviar imagem');
        const data = await res.json();
        setImages(prev => [...prev, data]);
      } catch (e) {
        setError(e.message);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!confirm('Remover esta imagem do hero?')) return;
    try {
      const res = await fetch(`/admin/hero_images/${id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken() },
      });
      if (!res.ok) throw new Error('Erro ao remover');
      setImages(prev => prev.filter(img => img.id !== id));
    } catch (e) {
      setError(e.message);
    }
  };

  const moveImage = async (index, direction) => {
    const newImages = [...images];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newImages.length) return;
    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
    setImages(newImages);

    try {
      await fetch('/admin/hero_images/reorder', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: newImages.map(img => img.id) }),
      });
    } catch (e) {
      setError('Erro ao reordenar');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleUpload(e.dataTransfer.files);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center gap-4 mb-6">
          <a href="/admin" className="text-gray-500 hover:text-gray-700 text-sm">← Painel</a>
          <h1 className="text-2xl font-bold text-gray-900">Imagens do Hero (Carrossel)</h1>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {/* Upload area */}
        <div
          className="mb-8 border-2 border-dashed border-orange-300 rounded-xl bg-orange-50 flex flex-col items-center justify-center py-10 cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <span className="text-4xl mb-3">🖼️</span>
          {uploading ? (
            <p className="text-orange-600 font-medium">Enviando...</p>
          ) : (
            <>
              <p className="text-orange-700 font-semibold">Clique ou arraste imagens aqui</p>
              <p className="text-orange-500 text-sm mt-1">JPG, PNG, WebP — múltiplos arquivos suportados</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>

        {/* Images grid */}
        {images.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-lg">Nenhuma imagem cadastrada ainda.</p>
            <p className="text-sm mt-1">Adicione imagens acima para exibir no carrossel da página inicial.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {images.map((img, index) => (
              <div key={img.id} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                <div className="relative" style={{ height: '200px' }}>
                  <img
                    src={img.image_url}
                    alt={`Hero ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs font-bold rounded px-2 py-1">
                    #{index + 1}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 gap-2">
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveImage(index, -1)}
                      disabled={index === 0}
                      className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-30 text-sm"
                      title="Mover para a esquerda"
                    >
                      ←
                    </button>
                    <button
                      onClick={() => moveImage(index, 1)}
                      disabled={index === images.length - 1}
                      className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 disabled:opacity-30 text-sm"
                      title="Mover para a direita"
                    >
                      →
                    </button>
                  </div>
                  <button
                    onClick={() => handleDelete(img.id)}
                    className="px-3 py-1 rounded bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium transition-colors"
                  >
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroImagesManager;
